import { ComponentPortal } from '@angular/cdk/portal';
import { _getEventTarget } from '@angular/cdk/platform';
import {
  input,
  signal,
  effect,
  inject,
  Injector,
  Directive,
  OnDestroy,
  ElementRef,
  InputSignal,
  WritableSignal,
  ViewContainerRef,
} from '@angular/core';
import {
  Overlay,
  OverlayRef,
  OverlayConfig,
  ConnectedPosition,
  VerticalConnectionPos,
  HorizontalConnectionPos,
  FlexibleConnectedPositionStrategy,
} from '@angular/cdk/overlay';

import { MenuItems, MenuPositionX, MenuPositionY } from './types';

import { Menu } from './menu';
import { MENU_DATA } from './menu-injectors';

@Directive({
  selector: '[appMenu]',
  host: {
    '(click)': 'toggle($event)',
  },
  standalone: true,
})
export class MenuDirective implements OnDestroy {
  private overlay: Overlay = inject(Overlay);
  private viewContainer: ViewContainerRef = inject(ViewContainerRef);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  public menuItems: InputSignal<MenuItems> = input<MenuItems>([], {
    alias: 'appMenu',
  });

  public context: InputSignal<unknown> = input<unknown>(undefined, {
    alias: 'appMenuContext',
  });

  public xPosition: InputSignal<MenuPositionX> = input<MenuPositionX>('before');

  public yPosition: InputSignal<MenuPositionY> = input<MenuPositionY>('below');

  protected appMenuClass = input<string | string[] | Set<string> | Record<string, any>>('');

  private opened: WritableSignal<boolean> = signal(false);

  private closed: WritableSignal<boolean> = signal(true);

  private overlayRef: OverlayRef | null = null;

  private menuInstance!: Menu | null;

  constructor() {
    effect(() => {
      if (this.menuInstance) {
        this.menuInstance.menuClass = this.appMenuClass();
      }
    });

    effect(() => {
      if (this.closed()) {
        this.overlayRef?.detach();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.menuInstance = null;
    }
  }

  toggle(event: Event) {
    event.stopPropagation();

    if (this.isOpen()) {
      this.close();

      return;
    }

    this.open();
  }

  isOpen() {
    return !!this.overlayRef?.hasAttached();
  }

  open() {
    if (this.isOpen() || !this.menuItems()?.length) {
      return;
    }

    this.opened.set(true);
    this.closed.set(false);

    this.overlayRef = this.overlayRef || this.overlay.create(this.getOverlayConfig());

    const injector = Injector.create({
      providers: [
        {
          provide: MENU_DATA,
          useValue: {
            items: this.menuItems(),
            context: this.context(),
            closeHandler: this.close.bind(this),
          },
        },
      ],
    });

    const component = new ComponentPortal(Menu, this.viewContainer, injector);
    this.menuInstance = this.overlayRef.attach(component).instance;

    this.subscribeToOutsideClicks();
  }

  close() {
    if (!this.isOpen()) {
      return;
    }

    this.closed.set(true);
    this.opened.set(false);
  }

  private subscribeToOutsideClicks() {
    if (this.overlayRef) {
      this.overlayRef.outsidePointerEvents().subscribe((event) => {
        const target = _getEventTarget(event) as Element;
        const element = this.elementRef.nativeElement;

        if (target !== element && !element.contains(target)) {
          this.close();
        }
      });
    }
  }

  private getOverlayConfig() {
    return new OverlayConfig({
      minWidth: this.elementRef.nativeElement.offsetWidth,
      positionStrategy: this.getOverlayPositionStrategy(),
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });
  }

  private getOverlayPositionStrategy(): FlexibleConnectedPositionStrategy {
    return this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withLockedPosition()
      .withGrowAfterOpen()
      .withPositions(this.getOverlayPositions());
  }

  private getOverlayPositions(): ConnectedPosition[] {
    const horizontalPosition: HorizontalConnectionPos[] =
      this.xPosition() === 'before' ? ['end', 'start'] : ['start', 'end'];

    const verticalPosition: VerticalConnectionPos[] =
      this.yPosition() === 'above' ? ['bottom', 'top'] : ['top', 'bottom'];

    const [originX, originFallbackX]: HorizontalConnectionPos[] = horizontalPosition;
    const [overlayY, overlayFallbackY]: VerticalConnectionPos[] = verticalPosition;

    let [originY, originFallbackY] = [overlayY, overlayFallbackY];
    const [overlayX, overlayFallbackX] = [originX, originFallbackX];
    let offsetY = 4;

    originY = overlayY === 'top' ? 'bottom' : 'top';
    originFallbackY = overlayFallbackY === 'top' ? 'bottom' : 'top';

    offsetY = overlayY === 'bottom' ? -offsetY : offsetY;

    return [
      { originX, originY, overlayX, overlayY, offsetY },
      {
        originX: originFallbackX,
        originY,
        overlayX: overlayFallbackX,
        overlayY,
        offsetY,
      },
      {
        originX,
        originY: originFallbackY,
        overlayX,
        overlayY: overlayFallbackY,
        offsetY: -offsetY,
      },
      {
        originX: originFallbackX,
        originY: originFallbackY,
        overlayX: overlayFallbackX,
        overlayY: overlayFallbackY,
        offsetY: -offsetY,
      },
    ];
  }
}
